import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Palette, Building2, Globe, Phone, Mail, MessageCircle, ImageIcon, Save, Check, Loader2, Eye, RotateCcw, UploadCloud, X, Copy, Download, QrCode } from "lucide-react";
import Layout from "../components/Layout";
import { api, auth } from "../lib/api";
import { useSettings } from "../lib/settings";
import { PRESET_COLORS } from "../lib/theme";

export default function AgencySettings() {
  const navigate = useNavigate();
  const user = auth.current();
  const { settings, refresh } = useSettings();
  const editSlug = user?.agency_slug || "default";
  const [form, setForm] = useState({
    agency_name: "",
    tagline: "",
    logo_url: "",
    primary_color: "#FF85AC",
    secondary_color: "#7C3AED",
    contact_email: "",
    contact_phone: "",
    whatsapp: "",
    website: "",
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);
  const fileInputRef = useRef(null);

  const clientLink = `${window.location.origin}/a/${editSlug}`;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(clientLink)}`;

  const copyClientLink = async () => {
    try {
      await navigator.clipboard.writeText(clientLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {}
  };

  const downloadQr = async () => {
    try {
      const resp = await fetch(qrSrc);
      const blob = await resp.blob();
      const objUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objUrl;
      link.download = `qr-${editSlug}.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objUrl);
    } catch {}
  };

  useEffect(() => {
    if (!user || !["master", "agency_admin"].includes(user.role)) { navigate("/admin/login"); return; }
    setForm({
      agency_name: settings.agency_name || "",
      tagline: settings.tagline || "",
      logo_url: settings.logo_url || "",
      primary_color: settings.primary_color || "#FF85AC",
      secondary_color: settings.secondary_color || "#7C3AED",
      contact_email: settings.contact_email || "",
      contact_phone: settings.contact_phone || "",
      whatsapp: settings.whatsapp || "",
      website: settings.website || "",
    });
  }, [settings.agency_name]);

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setSaved(false);
    if (k === "primary_color") {
      document.documentElement.style.setProperty("--brand-cyan", v);
    }
    if (k === "secondary_color") {
      document.documentElement.style.setProperty("--brand-secondary", v);
    }
  };

  const submit = async () => {
    setErr("");
    setSaving(true);
    try {
      await api.put(`/agencies/${editSlug}/settings`, form);
      await refresh();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const MAX_LOGO_BYTES = 500 * 1024; // 500 KB, debe coincidir con el límite del backend

  const handleLogoSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // permite volver a elegir el mismo archivo si falla
    if (!file) return;
    setLogoError("");

    if (!file.name.toLowerCase().endsWith(".png") || (file.type && file.type !== "image/png")) {
      setLogoError("El logo debe ser un archivo .png");
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setLogoError(`El archivo pesa ${(file.size / 1024).toFixed(0)} KB — el máximo es ${MAX_LOGO_BYTES / 1024} KB.`);
      return;
    }

    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/agencies/${editSlug}/upload-logo`, fd);
      update("logo_url", data.logo_url);
      await refresh();
    } catch (err) {
      setLogoError(err?.response?.data?.detail || "No se pudo subir el logo");
    } finally {
      setLogoUploading(false);
    }
  };

  const removeLogo = async () => {
    setLogoError("");
    try {
      await api.put(`/agencies/${editSlug}/settings`, { ...form, logo_url: "" });
      update("logo_url", "");
      await refresh();
    } catch (err) {
      setLogoError(err?.response?.data?.detail || "No se pudo quitar el logo");
    }
  };

  // Mismos valores por defecto que el modelo Agency en el backend — un
  // estado neutro de fábrica, no la marca de ninguna agencia real.
  const resetDefaults = () => {
    if (!confirm("¿Restaurar los valores por defecto de la plataforma?")) return;
    update("agency_name", "Travel Shield");
    update("tagline", "Viajes de Lujo · Protección Total");
    update("logo_url", "");
    update("primary_color", "#FF85AC");
    update("secondary_color", "#7C3AED");
    update("contact_email", "soporte@atiktech.com");
    update("contact_phone", "+52 800 CHERRY");
    update("whatsapp", "");
    update("website", "https://atiktech.com");
  };

  if (!user) return null;

  const accent = form.primary_color || "#FF85AC";

  return (
    <Layout>
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-12 space-y-10">
        <div className="fade-up">
          <p className="text-xs uppercase tracking-[0.4em]" style={{ color: accent }}>Marca Blanca · White Label</p>
          <h1 className="font-display text-5xl font-light tracking-tight mt-3">
            Configuración de <span style={{ color: accent }}>Agencia</span>
          </h1>
          <p className="text-neutral-400 mt-3 max-w-2xl">
            Personaliza nombre, logo, color principal y datos de contacto. Los cambios se aplican
            instantáneamente en toda la aplicación, incluyendo páginas públicas para tus clientes.
          </p>
        </div>

        <article className="glass rounded-3xl p-6 md:p-7 border border-white/10 flex flex-wrap items-center gap-6" data-testid="client-link-card">
          <img src={qrSrc} alt="QR de acceso para clientes" className="w-24 h-24 rounded-xl border border-white/10 bg-white p-1.5 shrink-0" />
          <div className="flex-1 min-w-[200px]">
            <div className="flex items-center gap-2 mb-1">
              <QrCode className="w-4 h-4" style={{ color: accent }} />
              <p className="text-xs uppercase tracking-[0.3em] text-neutral-500">Link para tus clientes</p>
            </div>
            <p className="text-sm text-neutral-300 break-all mb-3">{clientLink}</p>
            <div className="flex items-center gap-4 flex-wrap">
              <button
                onClick={copyClientLink}
                data-testid="copy-client-link-btn"
                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] hover:underline"
                style={{ color: accent }}
              >
                {linkCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {linkCopied ? "Copiado" : "Copiar link"}
              </button>
              <button
                onClick={downloadQr}
                data-testid="download-client-qr-btn"
                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-neutral-400 hover:text-white"
              >
                <Download className="w-3.5 h-3.5" /> Descargar QR
              </button>
            </div>
          </div>
        </article>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Identity */}
            <article className="glass rounded-3xl p-7 border border-white/10">
              <div className="flex items-center gap-3 mb-5">
                <Building2 className="w-5 h-5" style={{ color: accent }} strokeWidth={1.5} />
                <h2 className="font-display text-xl">Identidad</h2>
              </div>
              <div className="space-y-5">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 block mb-1.5">Nombre de la agencia</label>
                  <input
                    value={form.agency_name}
                    onChange={(e) => update("agency_name", e.target.value)}
                    data-testid="agency-name-input"
                    className="w-full bg-transparent border-b border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none py-2 text-white"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 block mb-1.5">Tagline / Eslogan</label>
                  <input
                    value={form.tagline}
                    onChange={(e) => update("tagline", e.target.value)}
                    data-testid="tagline-input"
                    className="w-full bg-transparent border-b border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none py-2 text-white"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 block mb-1.5">Logotipo (PNG con fondo transparente)</label>
                  <div className="flex items-center gap-4">
                    <div
                      className="w-16 h-16 rounded-xl border border-white/15 flex items-center justify-center shrink-0 overflow-hidden"
                      style={{
                        backgroundImage:
                          "linear-gradient(45deg, #222 25%, transparent 25%), linear-gradient(-45deg, #222 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #222 75%), linear-gradient(-45deg, transparent 75%, #222 75%)",
                        backgroundSize: "10px 10px",
                        backgroundPosition: "0 0, 0 5px, 5px -5px, -5px 0px",
                        backgroundColor: "#000",
                      }}
                      title="Fondo a cuadros = así se ve la transparencia"
                    >
                      {form.logo_url ? (
                        <img src={form.logo_url} alt="Logo" className="max-w-full max-h-full object-contain" />
                      ) : (
                        <ImageIcon className="w-5 h-5 text-neutral-600" />
                      )}
                    </div>

                    <div className="flex-1 space-y-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,.png"
                        onChange={handleLogoSelect}
                        className="hidden"
                        data-testid="logo-file-input"
                      />
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={logoUploading}
                          data-testid="logo-upload-btn"
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 hover:border-white/30 text-xs disabled:opacity-50"
                        >
                          {logoUploading ? (
                            <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Subiendo…</>
                          ) : (
                            <><UploadCloud className="w-3.5 h-3.5" /> Seleccionar PNG</>
                          )}
                        </button>
                        {form.logo_url && (
                          <button
                            type="button"
                            onClick={removeLogo}
                            data-testid="logo-remove-btn"
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs text-neutral-400 hover:text-white"
                          >
                            <X className="w-3.5 h-3.5" /> Quitar
                          </button>
                        )}
                      </div>
                      <p className="text-[10px] text-neutral-500 leading-relaxed">
                        Debe tener fondo transparente real (no un cuadro blanco) · máx. {MAX_LOGO_BYTES / 1024} KB · máx. 1000×1000px
                      </p>
                      {logoError && (
                        <p className="text-xs text-[#FF3B30]" data-testid="logo-error">{logoError}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </article>

            {/* Color */}
            <article className="glass rounded-3xl p-7 border border-white/10">
              <div className="flex items-center gap-3 mb-5">
                <Palette className="w-5 h-5" style={{ color: accent }} strokeWidth={1.5} />
                <h2 className="font-display text-xl">Color de Acento</h2>
              </div>

              <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 block mb-2">Primario</label>
              <div className="flex items-center gap-4 mb-4">
                <input
                  type="color"
                  value={form.primary_color}
                  onChange={(e) => update("primary_color", e.target.value)}
                  data-testid="color-picker"
                  className="w-16 h-12 rounded-lg bg-transparent border border-white/15 cursor-pointer"
                />
                <input
                  value={form.primary_color}
                  onChange={(e) => update("primary_color", e.target.value)}
                  data-testid="color-hex-input"
                  className="font-mono text-sm bg-transparent border-b border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none py-2 text-white w-32"
                />
              </div>
              <div className="flex flex-wrap gap-2 mb-6">
                {PRESET_COLORS.map((c) => (
                  <button
                    key={c}
                    onClick={() => update("primary_color", c)}
                    data-testid={`preset-color-${c}`}
                    className={`w-9 h-9 rounded-full border-2 transition-transform hover:scale-110 ${
                      form.primary_color.toLowerCase() === c.toLowerCase() ? "border-white" : "border-transparent"
                    }`}
                    style={{ backgroundColor: c }}
                    title={c}
                  />
                ))}
              </div>

              <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 block mb-2">
                Secundario <span className="normal-case tracking-normal text-neutral-600">— badges, estados y detalles</span>
              </label>
              <div className="flex items-center gap-4 mb-4">
                <input
                  type="color"
                  value={form.secondary_color}
                  onChange={(e) => update("secondary_color", e.target.value)}
                  data-testid="secondary-color-picker"
                  className="w-16 h-12 rounded-lg bg-transparent border border-white/15 cursor-pointer"
                />
                <input
                  value={form.secondary_color}
                  onChange={(e) => update("secondary_color", e.target.value)}
                  data-testid="secondary-color-hex-input"
                  className="font-mono text-sm bg-transparent border-b border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none py-2 text-white w-32"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                {PRESET_COLORS.map((c) => (
                  <button
                    key={`sec-${c}`}
                    onClick={() => update("secondary_color", c)}
                    data-testid={`preset-secondary-color-${c}`}
                    className={`w-9 h-9 rounded-full border-2 transition-transform hover:scale-110 ${
                      form.secondary_color.toLowerCase() === c.toLowerCase() ? "border-white" : "border-transparent"
                    }`}
                    style={{ backgroundColor: c }}
                    title={c}
                  />
                ))}
              </div>
            </article>

            {/* Contact */}
            <article className="glass rounded-3xl p-7 border border-white/10">
              <div className="flex items-center gap-3 mb-5">
                <Phone className="w-5 h-5" style={{ color: accent }} strokeWidth={1.5} />
                <h2 className="font-display text-xl">Datos de Contacto</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                {[
                  { k: "contact_email", label: "Email", icon: Mail, placeholder: "soporte@…" },
                  { k: "contact_phone", label: "Teléfono", icon: Phone, placeholder: "+52 800…" },
                  { k: "whatsapp", label: "WhatsApp", icon: MessageCircle, placeholder: "+52 555…" },
                  { k: "website", label: "Sitio web", icon: Globe, placeholder: "https://…" },
                ].map(({ k, label, icon: Icon, placeholder }) => (
                  <div key={k}>
                    <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 block mb-1.5">{label}</label>
                    <div className="flex items-center gap-3">
                      <Icon className="w-4 h-4 text-neutral-500 shrink-0" />
                      <input
                        value={form[k]}
                        onChange={(e) => update(k, e.target.value)}
                        placeholder={placeholder}
                        data-testid={`${k}-input`}
                        className="flex-1 bg-transparent border-b border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none py-2 text-white text-sm"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </article>

            {err && (
              <p className="text-sm text-[#FF3B30]" data-testid="settings-error">{err}</p>
            )}

            <div className="flex flex-wrap gap-3">
              <button
                onClick={submit}
                disabled={saving}
                data-testid="save-settings-btn"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold disabled:opacity-50 transition-all"
                style={{ backgroundColor: accent, color: "#000" }}
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {saved ? <><Check className="w-4 h-4" /> Guardado</> : <><Save className="w-4 h-4" /> Guardar cambios</>}
              </button>
              <button
                onClick={resetDefaults}
                data-testid="reset-defaults-btn"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border text-sm transition-colors"
                style={{ borderColor: `${form.secondary_color}66`, color: form.secondary_color }}
              >
                <RotateCcw className="w-4 h-4" /> Restaurar
              </button>
            </div>
          </div>

          {/* Live Preview */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="flex items-center gap-2 mb-4">
                <Eye className="w-4 h-4 text-neutral-500" />
                <p className="text-xs uppercase tracking-[0.3em] text-neutral-500">Vista previa</p>
              </div>
              <div className="rounded-3xl border border-white/10 overflow-hidden glass" data-testid="preview-card">
                <div className="h-2" style={{ background: `linear-gradient(90deg, ${accent}, ${form.secondary_color})` }} />
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    {form.logo_url ? (
                      <img src={form.logo_url} alt="" className="h-6 w-auto" />
                    ) : (
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: accent }} />
                    )}
                    <p className="font-display text-sm tracking-[0.2em] uppercase">
                      {form.agency_name || "Sin nombre"}
                    </p>
                  </div>
                  <p className="text-xs uppercase tracking-[0.3em] mb-3" style={{ color: accent }}>
                    {form.tagline || "Tagline"}
                  </p>
                  <button
                    type="button"
                    className="w-full py-2.5 rounded-full text-xs font-semibold"
                    style={{ backgroundColor: accent, color: "#000" }}
                  >
                    Botón de acción
                  </button>
                  <div className="mt-5 pt-5 border-t border-white/10 space-y-1.5 text-xs text-neutral-400">
                    {form.contact_email && <p><Mail className="inline w-3 h-3 mr-1.5" />{form.contact_email}</p>}
                    {form.contact_phone && <p><Phone className="inline w-3 h-3 mr-1.5" />{form.contact_phone}</p>}
                    {form.whatsapp && <p><MessageCircle className="inline w-3 h-3 mr-1.5" />{form.whatsapp}</p>}
                    {form.website && <p><Globe className="inline w-3 h-3 mr-1.5" />{form.website}</p>}
                  </div>
                </div>
              </div>
              <p className="text-[10px] text-neutral-600 mt-3 leading-relaxed">
                Los cambios se aplican en todo el frontend (header, botones, login y promociones) inmediatamente al guardar.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </Layout>
  );
}
